import { useTranslation } from 'react-i18next'
import * as Constants from '../../services/Constants'
import { useMemo } from 'react'
import { persistent } from '../../services/LocalStorage'

const defualtLangCode = 'en'

export function useI18nLanguageActions() {
    const { i18n } = useTranslation()

    return useMemo(() => {
        /**
         * Get system locale language.
         *
         * @returns Language code, e.g. en-US, ko-KR.
         */
        function detectSystemLanguage() {
            try {
                return Intl.DateTimeFormat().resolvedOptions().locale || defualtLangCode
            } catch (e) {
                return defualtLangCode
            }
        }

        /**
         * Gets preferred language among available languages defined in Constants.
         *
         * @param options
         * @returns An element in `Constants.availableLanguages` which the current preferred language
         * is the best fit, or `options.fallback` if provided, or `defualtLangCode` as a fallback of last resort.
         */
        function getPreferredLanguage(options?: { fallback: string }) {
            const preferred = persistent.getString('preferredLanguage') || detectSystemLanguage()
            const preferred_langCodeOnly = preferred.split('-')[0]
            if (Constants.availableLanguages.includes(preferred)) {
                return preferred
            }
            if (Constants.availableLanguages.includes(preferred_langCodeOnly)) {
                return preferred_langCodeOnly
            }

            // We also need to consider the case where preferred is 'en' and availableLanguages only has `defualtLangCode`.
            // in which case we need a reasonable default.
            for (let a of Constants.availableLanguages) {
                let code = a.split('-')[0]
                if (code === preferred) {
                    // use the current code as the reasonable fallback.
                    return a
                }
            }

            if (options?.fallback) {
                return options.fallback
            } else {
                return defualtLangCode
            }
        }

        /**
         * Sets the user's preferred language and saves it into the LocalStorage settings.
         *
         * @param code Full language code, e.g. en-US, ko-KR or language code only, e.g. "en", "kr".
         * @returns true
         */
        function setPreferredLanguage(code: string) {
            persistent.set('preferredLanguage', code)

            // this chooses the proper fallback language if the provided code is
            // not in availableLanguages.
            code = getPreferredLanguage()

            i18n.changeLanguage(code)
        }

        return {
            detectSystemLanguage,
            getPreferredLanguage,
            setPreferredLanguage,
        }
    }, [i18n])
}
